/**
 * useAuth Hook
 * React Query-based authentication hook
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth.store';
import { authService } from '../services/auth.service';
import type { LoginCredentials } from '../types';
import { handleApiError } from '../lib/axios';

export const useAuth = () => {
  const queryClient = useQueryClient();
  const { setAuth, clearAuth, user, member, isAuthenticated, isLoading } = useAuthStore();

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => authService.login(credentials),
    onSuccess: (data) => {
      setAuth(data.user, data.member, data.token, data.tenant?.name);
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error) => {
      console.error('Login failed:', handleApiError(error));
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      clearAuth();
      queryClient.clear(); // Clear all cached data
    },
    onError: (error) => {
      // Still clear auth even if server logout fails
      clearAuth();
      queryClient.clear();
      console.error('Logout error:', handleApiError(error));
    },
  });

  return {
    // State
    user,
    member,
    isAuthenticated,
    isLoading,

    // Actions
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,

    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,

    loginError: loginMutation.error ? handleApiError(loginMutation.error) : null,  };
};
